import { useTranslation } from "@sagot/i18n";
import { ChartLegend, LineChart, Skeleton, StackedBarChart, type StackSeries } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { alignedPrevious, axisTicks, calendarDate, hasActivity, weeklyStacks, type AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useTimeseries } from "./use-analytics";

function PlotSkeleton({ height }: { height: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className={height} />
      <div className="flex justify-between">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-3 w-10" />
        ))}
      </div>
    </div>
  );
}

/** Conversations per day: current period vs previous (dashed). */
export function ConversationsTrendCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useTimeseries(filters, "day");
  const day = (iso: string) => fmt.date(calendarDate(iso));
  const period = query.data?.period;
  return (
    <ChartCard
      className="xl:col-span-2"
      testId="analytics-trend"
      title={t("engage.analytics.trend.title")}
      subtitle={period ? t("engage.analytics.trend.subtitle", { start: day(period.start), end: day(period.end) }) : undefined}
      aside={
        <ChartLegend
          items={[
            { label: t("engage.analytics.trend.current"), tone: "brand", shape: "line" },
            { label: t("engage.analytics.trend.previous"), tone: "ink-light", shape: "line" },
          ]}
        />
      }
      query={query}
      isEmpty={(data) => !hasActivity(data.current) && !hasActivity(data.previous)}
      empty={{ icon: "messages-square", title: t("engage.analytics.trend.emptyTitle"), body: t("engage.analytics.trend.emptyBody") }}
      skeleton={<PlotSkeleton height="h-60" />}
    >
      {(data) => {
        const labels = data.current.map((p) => day(p.date));
        return (
          <LineChart
            title={t("engage.analytics.trend.title")}
            labels={labels}
            axisLabels={axisTicks(labels)}
            showLegend={false}
            formatValue={fmt.number}
            series={[
              { label: t("engage.analytics.trend.current"), tone: "brand", values: data.current.map((p) => p.conversations) },
              { label: t("engage.analytics.trend.previous"), tone: "ink-light", dashed: true, values: alignedPrevious(data.current, data.previous) },
            ]}
          />
        );
      }}
    </ChartCard>
  );
}

/** Weekly stacked bars: AI resolved / handed to staff / abandoned. */
export function WeeklyOutcomesCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useTimeseries(filters, "week");
  const day = (iso: string) => fmt.date(calendarDate(iso));
  const series: StackSeries[] = [
    { key: "ai_resolved", label: t("engage.analytics.weekly.aiResolved"), tone: "brand" },
    { key: "handed_off", label: t("engage.analytics.weekly.handedOff"), tone: "signal" },
    { key: "abandoned", label: t("engage.analytics.weekly.abandoned"), tone: "ink-light" },
  ];
  return (
    <ChartCard
      testId="analytics-weekly"
      title={t("engage.analytics.weekly.title")}
      subtitle={t("engage.analytics.weekly.subtitle")}
      query={query}
      isEmpty={(data) => !hasActivity(data.current)}
      empty={{ icon: "bar-chart", title: t("engage.analytics.trend.emptyTitle"), body: t("engage.analytics.trend.emptyBody") }}
      skeleton={<PlotSkeleton height="h-56" />}
    >
      {(data) => {
        const stacks = weeklyStacks(data.current, day);
        return (
          <div>
            <StackedBarChart title={t("engage.analytics.weekly.title")} series={series} data={stacks} axisLabels={axisTicks(stacks.map((s) => s.label))} showLegend={false} formatValue={fmt.number} />
            <ChartLegend className="mt-4" items={series.map((s) => ({ label: s.label, tone: s.tone }))} />
          </div>
        );
      }}
    </ChartCard>
  );
}
